import { Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AutoResume, DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { BehaviorSubject, catchError, of } from 'rxjs';
import { environment } from '@uw-env/environment';
import {
  IdleWarningDialogComponent,
  IdleWarningResult,
} from '../../shared/components/idle-warning-dialog/idle-warning-dialog.component';
import { AuthService } from './auth.service';
import { CustomNavigationService } from '../custom-navigation.service';

// The warning appears at (total - warning) seconds of inactivity; logout follows when the warning countdown ends.
const TOTAL_SECONDS = environment.ngIdleConfig.inactivityTimeoutMinutes * 60;
const TIMEOUT_SECONDS = Math.min(environment.ngIdleConfig.warningSeconds, TOTAL_SECONDS - 1);
const IDLE_SECONDS = TOTAL_SECONDS - TIMEOUT_SECONDS;

@Injectable({ providedIn: 'root' })
export class IdleLogoutService {
  private initialized = false;
  private dialogRef: MatDialogRef<IdleWarningDialogComponent, IdleWarningResult> | null = null;
  private readonly secondsRemaining$ = new BehaviorSubject<number>(TIMEOUT_SECONDS);

  constructor(
    private idle: Idle,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private authService: AuthService,
    private customNavigationService: CustomNavigationService
  ) {}

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.idle.setIdle(IDLE_SECONDS);
    this.idle.setTimeout(TIMEOUT_SECONDS);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    // Once the warning is shown, only an explicit button click keeps the session alive.
    this.idle.setAutoResume(AutoResume.notIdle);

    this.idle.onIdleStart.subscribe(() => this.ngZone.run(() => this.showWarning()));
    this.idle.onTimeoutWarning.subscribe((seconds) =>
      this.ngZone.run(() => this.secondsRemaining$.next(seconds))
    );
    this.idle.onIdleEnd.subscribe(() => this.ngZone.run(() => this.closeWarning()));
    this.idle.onTimeout.subscribe(() => this.ngZone.run(() => this.handleTimeout()));
  }

  private isLoggedIn(): boolean {
    return !!localStorage.getItem('token');
  }

  private showWarning(): void {
    if (!this.isLoggedIn() || this.dialogRef) return;

    this.secondsRemaining$.next(TIMEOUT_SECONDS);
    this.dialogRef = this.dialog.open(IdleWarningDialogComponent, {
      width: '450px',
      disableClose: true,
      data: { secondsRemaining$: this.secondsRemaining$.asObservable() },
    });

    this.dialogRef.afterClosed().subscribe((result) => {
      this.dialogRef = null;
      if (result === 'logout') {
        this.forceLogout();
      } else if (result === 'stay') {
        this.idle.watch();
      }
    });
  }

  private closeWarning(): void {
    this.dialogRef?.close();
  }

  private handleTimeout(): void {
    this.closeWarning();
    if (this.isLoggedIn()) {
      this.forceLogout();
    }
    this.idle.watch();
  }

  private forceLogout(): void {
    const isSSOUser = localStorage.getItem('isSSOUser') === 'true';
    const loginRoute = isSSOUser ? 'sso-login' : 'login';
    const goToLogin = () => this.customNavigationService.navigate(['/main', loginRoute]);

    if (!this.authService.getUser()) {
      this.authService.clearStorage(isSSOUser, true);
      goToLogin();
      return;
    }

    this.authService
      .logOut()
      .pipe(
        // Logout must complete locally even if the API call fails.
        catchError(() => {
          this.authService.cleanUpAuthData();
          return of(null);
        })
      )
      .subscribe(() => goToLogin());
  }
}
